import {
  peakBaseShadow,
  peakDaybreak,
  peakDaybreakBase,
  peakDaybreakBgConnect,
  peakDaybreakBgHome,
  peakDaybreakChamberXL,
} from '../assets/images';
import {addOpacityToColorHex} from '../lib/utils/colors/addOpacityToColorHex';
import {colors} from '../styles';
import {defaultTheme} from './DefaultTheme';
import {stormTheme} from './StormTheme';
import {
  ConnectScreenTheme,
  ControlCenterTheme,
  DabbingScreenTheme,
  HeatProfileCardTheme,
  HeatProfileEditScreenTheme,
  HeatProfileListScreenTheme,
  HeatProfileSelectScreenTheme,
  HomeScreenTheme,
  LimitedEditionModalScreenTheme,
  LimitedEditionVideoScreenTheme,
  PeakImageTheme,
  TabBarTheme,
  Theme,
} from './Theme';

const LINEAR_GRADIENT_ARRAY = ['#521625', '#924345'];

const {lightLayers} = stormTheme.peakImageTheme;

const peakImageTheme: PeakImageTheme = {
  ...defaultTheme.peakImageTheme,
  peak: peakDaybreak,
  peakLayers: {
    base: peakDaybreakBase,
    chamber: {
      regular: peakDaybreakChamberXL,
      xl: peakDaybreakChamberXL,
    },
    shadow: peakBaseShadow,
  },
  lightLayers,

  peakDarkScreen: peakDaybreak,
};

const tabBarTheme: TabBarTheme = {
  ...defaultTheme.tabBarTheme,
  activeColor: colors.white,
  inactiveColor: 'rgba(255, 255, 255, 0.5)',
  backgroundColor: '#924345',
};

const heatProfileCardTheme: HeatProfileCardTheme = {
  ...defaultTheme.heatProfileCardTheme,
  durationBubbleColor: colors.black,
  durationFontColor: colors.white,
  specularColorStart: colors.clear,
  specularColorEnd: colors.clear,
};
/* endregion: Components */

/* region: Screens */
const connectScreenTheme: ConnectScreenTheme = {
  ...defaultTheme.connectScreenTheme,
  background: {
    image: peakDaybreakBgConnect,
    gradient: LINEAR_GRADIENT_ARRAY,
  },
};

const limitedEditionModalScreenTheme: LimitedEditionModalScreenTheme = {
  background: {
    image: peakDaybreakBgConnect,
    gradient: LINEAR_GRADIENT_ARRAY,
  },
};

const limitedEditionVideoScreenTheme: LimitedEditionVideoScreenTheme = {
  videoUrl:
    'https://puffco-prod.s3.us-west-2.amazonaws.com/welcome/daybreak.mp4',
};

const heatProfileListScreenTheme: HeatProfileListScreenTheme = {
  background: {
    image: peakDaybreakBgConnect,
    gradient: LINEAR_GRADIENT_ARRAY,
  },
  hoverBackgroundColor: colors.gray,
};

const homeScreenTheme: HomeScreenTheme = {
  ...defaultTheme.homeScreenTheme,

  // General
  background: {
    gradient: LINEAR_GRADIENT_ARRAY,
    image: peakDaybreakBgHome,
  },

  // Status info
  batteryBarBackgroundColor: colors.mediumGray50,
  batteryDisconnectedColor: addOpacityToColorHex(colors.white, 0.5),
  preserveIndicator: colors.black,
  statusTextStealthColor: colors.black,
  statusTitleTextColor: colors.white,
  stealthIconColor: colors.black,

  // Metric info
  dataBackgroundGradientArray: [
    {color: '#6D2B33', location: 0.15},
    {color: '#FCC583', location: 0.75},
    {color: '#00000000', location: 1},
  ],
  metricDisconnectedTextColor: addOpacityToColorHex(colors.white, 0.5),
  metricTextColor: colors.white,
  metricTitleTextColor: addOpacityToColorHex(colors.white, 0.5),
};

const dabbingScreenTheme: DabbingScreenTheme = {
  dabbingScreenActiveText: colors.white,
  dabbingScreenFadedText: colors.mediumGray50,
  dabbingButtonBackground: colors.bone,
  dabbingButtonBackgroundPressed: colors.black,
  dabbingButtonForeground: colors.black,
  dabbingButtonForegroundPressed: colors.bone,
  iconColor: colors.white,
  textColor: colors.white,
  fadedTextColor: colors.mediumGray50,
};

const heatProfileEditScreenTheme: HeatProfileEditScreenTheme = {
  ...defaultTheme.heatProfileEditScreenTheme,
  editTextColor: colors.white,
  iconColor: colors.white,
  navMenuTitleStyle: {
    ...defaultTheme.navMenuTitleStyle,
    color: colors.white,
  },
};

const heatProfileSelectScreenTheme: HeatProfileSelectScreenTheme = {
  primaryColor: colors.white,
  durationColor: colors.white50,
  startTextStyle: {color: colors.white, fontWeight: '400'},
  backgroundLineColor: colors.gray,
};

const controlCenterTheme: ControlCenterTheme = {
  titleText: colors.white,
  infoText: colors.white,
  fatSliderColor: colors.white,
  fatSliderBGColor: colors.black,
  buttonActiveForeground: colors.black,
  buttonInactiveForeground: colors.white,
  buttonActiveBackground: colors.white,
  buttonInactiveBackground: colors.black50,
  blurBackgroundColor: 'rgba(51, 51, 51, 0.8)',
  background: {
    color: 'rgba(51, 51, 51, 0.8)',
  },
  boostScreenLabel: colors.baseText,
};
/* endregion: Screens */

export const daybreakTheme: Theme = {
  ...defaultTheme,

  // Components
  primaryColor: colors.white,
  primaryTextColor: colors.white,
  logoTint: colors.invisible,
  peakImageTheme,
  heatProfileCardTheme,
  sectionDividerColor: colors.white20,
  sectionTitleTextColor: colors.white,
  statusDisplayTextColor: colors.white,
  spinnerColor: colors.black,
  tabBarTheme,
  styledButtonTheme: 'white',

  // Screens
  assignToAHeatProfileScreenTheme: {
    ...defaultTheme.assignToAHeatProfileScreenTheme,
    background: {
      image: peakDaybreakBgConnect,
      gradient: LINEAR_GRADIENT_ARRAY,
    },
  },
  connectScreenTheme,
  limitedEditionModalScreenTheme,
  limitedEditionVideoScreenTheme,
  homeScreenTheme,
  heatProfileListScreenTheme,
  dabbingScreenTheme,
  heatProfileEditScreenTheme,
  heatProfileSelectScreenTheme,
  controlCenterTheme,

  dabTickTheme: {
    color: colors.white,
  },
};
